import { useState, useEffect, useCallback, useRef } from "react";
import { Search, ExternalLink, Globe, ChevronLeft, ChevronRight, RefreshCw, Zap, Target, Filter, TrendingUp } from "lucide-react";
import { TOKENS, BADGE_BASE, BADGE, sectorChipStyle } from "../tokens";
import { useThemedTokens } from "../theme";
import WatchedFunders from "./WatchedFunders";
import { API, apiFetch } from "../utils/api";

const ORG_KEY = "orgProfile_v1";
const getOrgProfile = () => { try { return JSON.parse(localStorage.getItem(ORG_KEY) || "null"); } catch { return null; } };

const LIMIT = 20;

// Session cache: revisiting Discover shows the last results instantly
// (no skeleton flash) while a background refresh reconciles. Keyed by the
// full filter state; 5-minute TTL; clears when the browser closes.
const DISC_CACHE_KEY = "nofodesk_discover_cache_v1";
const DISC_CACHE_TTL = 5 * 60 * 1000;
function loadDiscoverCache(key) {
  try {
    const all = JSON.parse(sessionStorage.getItem(DISC_CACHE_KEY) || "{}");
    const hit = all[key];
    return hit && Date.now() - hit.ts < DISC_CACHE_TTL ? hit : null;
  } catch { return null; }
}
function saveDiscoverCache(key, grants, total) {
  try {
    const all = JSON.parse(sessionStorage.getItem(DISC_CACHE_KEY) || "{}");
    const keys = Object.keys(all);
    if (keys.length > 8) delete all[keys[0]]; // keep the store small
    all[key] = { grants, total, ts: Date.now() };
    sessionStorage.setItem(DISC_CACHE_KEY, JSON.stringify(all));
  } catch {}
}

/* Skeleton shimmer block — static, built once (keyframes live in App.css) */
const shimmerStyle = (T) => ({
  borderRadius: 4,
  background: `linear-gradient(90deg, ${T.shimmerBase} 25%, ${T.shimmerHighlight} 50%, ${T.shimmerBase} 75%)`,
  backgroundSize: "800px 100%",
  animation: "shimmer 1.4s ease-in-out infinite",
});

const SOURCE_DISPLAY = { "Brave Search": "Web Search" };

const SECTORS = ["Health", "Education", "Agriculture", "Environment", "WASH", "Governance", "Livelihoods", "Gender", "Humanitarian", "Technology"];
const GRANT_TYPES = ["Grant", "Fellowship", "Loan", "Prize", "RFP", "Capacity Building"];

// Source badges: outline style, mono, textMuted — visually distinct from
// sector chips (design brief §7)
function SourceBadge({ source }) {
  const T = useThemedTokens();
  const display = SOURCE_DISPLAY[source] || source;
  return (
    <span style={{ ...BADGE_BASE, fontSize: "10px", padding: "2px 8px", background: "transparent", color: T.textMuted, border: `1px solid ${T.border}`, fontFamily: T.fontMono, letterSpacing: "0.04em" }}>
      {display}
    </span>
  );
}

// Deadline chips share the Tracker urgency thresholds: red ≤7d, amber ≤30d
function DeadlineBadge({ deadline }) {
  const T = useThemedTokens();
  if (!deadline) return null;
  const d = new Date(deadline);
  const now = new Date();
  const days = Math.ceil((d - now) / 86400000);
  const monoNum = { fontFamily: T.fontMono, fontVariantNumeric: "tabular-nums" };
  if (days < 0) return <span style={{ ...BADGE_BASE, ...BADGE.red, ...monoNum, fontSize: "11px", padding: "2px 8px", opacity: 0.6 }}>Closed</span>;
  if (days === 0) return <span style={{ ...BADGE_BASE, ...BADGE.red, ...monoNum, fontSize: "11px", padding: "2px 8px" }}>Due today</span>;
  if (days <= 7)  return <span style={{ ...BADGE_BASE, ...BADGE.red,   ...monoNum, fontSize: "11px", padding: "2px 8px" }}>{days}d left</span>;
  if (days <= 30) return <span style={{ ...BADGE_BASE, ...BADGE.amber, ...monoNum, fontSize: "11px", padding: "2px 8px" }}>{days}d left</span>;
  return <span style={{ ...BADGE_BASE, ...BADGE.muted, ...monoNum, fontSize: "11px", padding: "2px 8px" }}>{d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>;
}

// Fit levels map to system colors: excellent=success, good=accent,
// partial/poor=muted (design brief §7 — accent never used for categorization,
// but fit is an interaction-quality signal, not a category)
const FIT_BADGES = {
  excellent: { bg: "rgba(34,212,168,0.12)",  color: "#22D4A8", border: "rgba(34,212,168,0.3)",  label: "Excellent match" },
  good:      { bg: "rgba(124,115,240,0.12)", color: "#7C73F0", border: "rgba(124,115,240,0.3)", label: "Good match"      },
  partial:   { bg: "rgba(107,104,168,0.12)", color: "#6B68A8", border: "rgba(107,104,168,0.25)", label: "Partial match"  },
  poor:      { bg: "rgba(107,104,168,0.08)", color: "#6B68A8", border: "rgba(107,104,168,0.18)", label: "Low match"      },
};

function FitBadge({ fitLevel }) {
  const T = useThemedTokens();
  const b = FIT_BADGES[fitLevel];
  if (!b) return null;
  return (
    <span style={{ ...BADGE_BASE, fontSize: "10px", padding: "2px 8px", background: b.bg, color: b.color, border: `1px solid ${b.border}`, fontFamily: T.fontMono, letterSpacing: "0.03em" }}>
      {b.label}
    </span>
  );
}

function ConfidenceDot({ score }) {
  const T = useThemedTokens();
  if (!score) return null;
  const pct = Math.round(score * 100);
  const col = pct >= 70 ? "#22D4A8" : pct >= 40 ? "#F5A623" : "#6B68A8";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "11px", color: col, fontFamily: T.fontUI }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: col, display: "inline-block" }} />
      {pct}% match
    </span>
  );
}

function GrantCard({ grant, onImport, onExtract, matchMode }) {
  const T = useThemedTokens();
  const [expanded, setExpanded] = useState(false);
  const [whyOpen,  setWhyOpen]  = useState(false);

  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, padding: "20px", display: "flex", flexDirection: "column", gap: "10px", transition: "border-color 0.15s", boxSizing: "border-box", maxWidth: "100%" }}
      onMouseEnter={e => e.currentTarget.style.borderColor = T.borderLight}
      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
    >
      {/* Title row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
        <h3 style={{ fontFamily: T.fontUI, fontSize: "15px", fontWeight: 600, color: T.textPrimary, lineHeight: 1.4, margin: 0, flex: 1 }}>
          {grant.title}
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end", flexShrink: 0 }}>
          <SourceBadge source={grant.source} />
          {matchMode && grant.fit_level && <FitBadge fitLevel={grant.fit_level} />}
          {matchMode && !grant.fit_level && <ConfidenceDot score={grant.confidence} />}
        </div>
      </div>

      {/* Meta row: funder → deadline → amount (unified card hierarchy) */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
        {grant.funder && <span style={{ fontSize: "13px", color: T.textSecondary, fontFamily: T.fontUI }}>{grant.funder}</span>}
        {grant.region && <span style={{ fontSize: "12px", color: T.textMuted, fontFamily: T.fontUI }}>· {grant.region}</span>}
        <DeadlineBadge deadline={grant.deadline} />
        {grant.amount_text && <span style={{ ...BADGE_BASE, ...BADGE.muted, fontSize: "11px", padding: "2px 8px", fontFamily: T.fontMono, fontVariantNumeric: "tabular-nums" }}>{grant.amount_text}</span>}
        {grant.grant_type && grant.grant_type !== "Grant" && (
          <span style={{ ...BADGE_BASE, fontSize: "10px", padding: "2px 8px", background: "transparent", color: T.textMuted, border: `1px solid ${T.border}`, fontFamily: T.fontMono }}>{grant.grant_type}</span>
        )}
      </div>

      {/* Sector + focus areas — functional dim pattern, mono labels */}
      {(grant.sector || grant.focus_areas?.length > 0) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
          {grant.sector && (
            <span style={{ ...BADGE_BASE, fontSize: "10px", padding: "2px 8px", letterSpacing: "0.04em", ...sectorChipStyle(grant.sector) }}>{grant.sector}</span>
          )}
          {grant.focus_areas?.slice(0, 3).map((a, i) => (
            <span key={i} style={{ ...BADGE_BASE, ...BADGE.muted, fontSize: "11px", padding: "2px 8px" }}>{a}</span>
          ))}
        </div>
      )}

      {/* Description */}
      {grant.description && (
        <p
          onClick={() => setExpanded(e => !e)}
          style={{ fontSize: "13px", color: T.textSecondary, fontFamily: T.fontUI, lineHeight: 1.6, margin: 0, cursor: "pointer",
            ...(!expanded ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" } : {}) }}
        >
          {grant.description}
        </p>
      )}

      {/* "Why this match?" expand — reason in Inter (operational, not document) */}
      {matchMode && grant.match_reason && (
        <div>
          <button
            onClick={() => setWhyOpen(o => !o)}
            aria-expanded={whyOpen}
            style={{ background: "none", border: "none", padding: 0, fontSize: "12px", color: "#7C73F0", fontFamily: T.fontUI, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, minHeight: 24 }}>
            <span aria-hidden="true" style={{ fontSize: 9 }}>{whyOpen ? "▼" : "▶"}</span> Why this match?
          </button>
          {whyOpen && (
            <div style={{ marginTop: 6, padding: "8px 12px", background: "rgba(124,115,240,0.06)", border: "1px solid rgba(124,115,240,0.18)", borderRadius: 6 }}>
              <span style={{ fontSize: "12px", color: T.textSecondary, fontFamily: T.fontUI, lineHeight: 1.5 }}>{grant.match_reason}</span>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: "8px", marginTop: "4px", flexWrap: "wrap" }}>
        <a
          href={grant.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "7px 14px", background: "transparent", border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: T.textSecondary, fontSize: "13px", fontFamily: T.fontUI, cursor: "pointer", textDecoration: "none" }}
        >
          <ExternalLink size={13} />
          Open
        </a>
        <button
          onClick={() => onImport(grant)}
          style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "7px 14px", background: "rgba(107,104,168,0.1)", border: `1px solid rgba(107,104,168,0.25)`, borderRadius: T.radiusMd, color: T.textMuted, fontSize: "13px", fontFamily: T.fontUI, cursor: "pointer" }}
        >
          <Zap size={13} />
          Quick Import
        </button>
        <button
          onClick={() => onExtract(grant)}
          style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "7px 14px", background: "rgba(91,79,233,0.15)", border: "1px solid rgba(91,79,233,0.35)", borderRadius: T.radiusMd, color: "#7C73F0", fontSize: "13px", fontFamily: T.fontUI, cursor: "pointer", fontWeight: 500 }}
        >
          <TrendingUp size={13} />
          Extract Full Details
        </button>
      </div>
    </div>
  );
}

export default function DiscoverView({ setActiveView, setInput, onExtractGrant }) {
  const T = useThemedTokens();
  const shimmer = shimmerStyle(T);
  const [grants,      setGrants]      = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [search,      setSearch]      = useState("");
  const [source,      setSource]      = useState("");
  const [sector,      setSector]      = useState("");
  const [grantType,   setGrantType]   = useState("");
  const [matchMode,   setMatchMode]   = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [status,      setStatus]      = useState(null);
  const [triggering,  setTriggering]  = useState(false);
  const [triggerMsg,  setTriggerMsg]  = useState(null);
  const [scanning,    setScanning]    = useState(false);
  // "all": the shared feed. "watched": this workspace's own funder pages.
  const [view,        setView]        = useState("all");
  const [watchedCount, setWatchedCount] = useState(null);
  const scanRef = useRef(null);

  const hasProfile = !!getOrgProfile();

  const load = useCallback(async () => {
    const cacheKey = JSON.stringify({ page, search, source, sector, grantType, matchMode });
    const cached   = loadDiscoverCache(cacheKey);
    if (cached) {
      // Instant render from the session cache; the fetch below reconciles.
      setGrants(cached.grants);
      setTotal(cached.total);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (search)    params.set("search", search);
      if (source)    params.set("source", source);
      if (sector)    params.set("sector", sector);
      if (grantType) params.set("grant_type", grantType);

      let endpoint = `${API}/discover`;
      if (matchMode && hasProfile) {
        const profile = getOrgProfile();
        endpoint = `${API}/discover/match`;
        const res = await apiFetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ profile, page, limit: LIMIT, search }),
        });
        if (res.ok) {
          const data = await res.json();
          setGrants(data.grants || []);
          setTotal(data.total || 0);
          saveDiscoverCache(cacheKey, data.grants || [], data.total || 0);
        }
      } else {
        const res = await apiFetch(`${endpoint}?${params}`);
        if (res.ok) {
          const data = await res.json();
          setGrants(data.grants || []);
          setTotal(data.total || 0);
          saveDiscoverCache(cacheKey, data.grants || [], data.total || 0);
        }
      }
    } catch {}
    setLoading(false);
  }, [page, search, source, sector, grantType, matchMode, hasProfile]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch(`${API}/discover/status`).then(r => r.ok ? r.json() : null).then(d => { if (d) setStatus(d); }).catch(() => {});
  }, []);

  // Pulse the scanning indicator for 90s after trigger
  useEffect(() => {
    return () => { if (scanRef.current) clearTimeout(scanRef.current); };
  }, []);

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerMsg(null);
    setScanning(true);
    try {
      const res  = await apiFetch(`${API}/discover/trigger`, { method: "POST" });
      const data = await res.json();
      if (res.ok) {
        setTriggerMsg({ type: "ok", text: "Discovery started — new grants appear in 1–2 minutes." });
        scanRef.current = setTimeout(() => {
          setScanning(false);
          load();
        }, 90000);
      } else {
        setScanning(false);
        setTriggerMsg({ type: "err", text: data.error || "Could not start discovery." });
      }
    } catch {
      setScanning(false);
      setTriggerMsg({ type: "err", text: "Could not reach server." });
    }
    setTriggering(false);
  };

  const handleImport = (grant) => {
    if (setInput)      setInput(grant.url);
    if (setActiveView) setActiveView("extract");
  };

  const handleExtract = (grant) => {
    if (onExtractGrant) {
      onExtractGrant(grant);
    } else {
      handleImport(grant);
    }
  };

  const totalPages  = Math.ceil(total / LIMIT);
  const lastFetched = status?.lastFetched
    ? new Date(status.lastFetched).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  const selectStyle = {
    padding: "9px 12px", background: T.bgCard, border: `1px solid ${T.border}`,
    borderRadius: T.radiusMd, color: T.textSecondary, fontSize: "14px",
    fontFamily: T.fontUI, minWidth: "140px", cursor: "pointer",
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h1 style={{ fontFamily: T.fontHeading, fontSize: "32px", fontWeight: 400, color: T.textPrimary, margin: 0, lineHeight: 1.2 }}>
              Grant Discovery
            </h1>
            <p style={{ fontSize: "13px", color: T.textMuted, fontFamily: T.fontUI, margin: "5px 0 0" }}>
              {scanning
                ? <span style={{ color: "#22D4A8", display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22D4A8", display: "inline-block", animation: "pulse 1.2s ease-in-out infinite" }} />
                    Scanning sources for new grants…
                  </span>
                : <>Grants.gov · EU Funding Portal · World Bank{status?.braveEnabled ? " · Live Web Search" : ""}</>
              }
            </p>
          </div>

          {/* Stats + Run Now */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            {status?.total > 0 && (
              <span style={{ ...BADGE_BASE, ...BADGE.muted, fontSize: "12px" }}>
                {status.total.toLocaleString()} grants indexed
              </span>
            )}
            {lastFetched && (
              <span style={{ fontSize: "12px", color: T.textMuted, fontFamily: T.fontUI }}>· Updated {lastFetched}</span>
            )}
            <button
              onClick={handleTrigger}
              disabled={triggering || scanning}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(91,79,233,0.12)", border: "1px solid rgba(91,79,233,0.3)", borderRadius: T.radiusMd, color: "#7C73F0", fontSize: "13px", fontFamily: T.fontUI, cursor: (triggering || scanning) ? "not-allowed" : "pointer", opacity: (triggering || scanning) ? 0.6 : 1 }}
            >
              <RefreshCw size={13} style={{ animation: (triggering || scanning) ? "spin 1s linear infinite" : "none" }} />
              {triggering ? "Starting…" : scanning ? "Scanning…" : "Run Now"}
            </button>
          </div>
        </div>

        {triggerMsg && (
          <div style={{ marginTop: "10px", padding: "9px 14px", background: triggerMsg.type === "ok" ? "rgba(34,212,168,0.08)" : "rgba(232,69,69,0.08)", border: `1px solid ${triggerMsg.type === "ok" ? "rgba(34,212,168,0.3)" : "rgba(232,69,69,0.3)"}`, borderRadius: T.radiusMd, fontSize: "13px", color: triggerMsg.type === "ok" ? "#22D4A8" : "#E84545", fontFamily: T.fontUI }}>
            {triggerMsg.text}
          </div>
        )}

        {!loading && grants.length === 0 && total === 0 && (
          <div style={{ marginTop: "14px", padding: "14px 18px", background: T.bgElevated, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.gold}`, borderRadius: T.radiusMd, fontSize: "13px", color: T.textSecondary, fontFamily: T.fontUI }}>
            <strong style={{ color: T.textPrimary }}>No grants discovered yet.</strong>
            {" "}Grant discovery runs automatically every day. Click <strong style={{ color: T.textPrimary }}>Run Now</strong> above to fetch the latest opportunities immediately.
          </div>
        )}
      </div>

      {/* ── Feed or watched pages ── */}
      <div role="tablist" style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: T.radiusMd, overflow: "hidden", marginBottom: "16px" }}>
        {[["all", "All sources"], ["watched", `Watched funders${watchedCount ? ` (${watchedCount})` : ""}`]].map(([id, text]) => (
          <button key={id} role="tab" aria-selected={view === id} onClick={() => setView(id)}
            style={{ padding: "8px 16px", border: "none", cursor: "pointer", fontSize: "13px", fontFamily: T.fontUI, fontWeight: 500,
              background: view === id ? "rgba(91,79,233,0.14)" : "transparent", color: view === id ? "#5B4FE9" : T.textMuted }}>
            {text}
          </button>
        ))}
      </div>

      {view === "watched" && <WatchedFunders onExtract={handleExtract} onCount={setWatchedCount} />}

      {view === "all" && (<>
      {/* ── Profile match toggle ── */}
      {hasProfile && (
        <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
          <button
            onClick={() => { setMatchMode(m => !m); setPage(1); }}
            style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "8px 16px", borderRadius: T.radiusMd, fontSize: "13px", fontFamily: T.fontUI, cursor: "pointer", transition: "all 0.15s",
              background: matchMode ? "rgba(91,79,233,0.18)" : "transparent",
              border: matchMode ? "1px solid rgba(91,79,233,0.5)" : `1px solid ${T.border}`,
              color: matchMode ? "#7C73F0" : T.textMuted,
            }}
          >
            <Target size={14} />
            Matched to My Profile
          </button>
          {matchMode && (
            <span style={{ fontSize: "12px", color: T.textMuted, fontFamily: T.fontUI }}>
              Showing grants ranked by relevance to your organisation profile
            </span>
          )}
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1", minWidth: "200px" }}>
          <Search size={14} style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", color: T.textMuted, pointerEvents: "none" }} />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search grants…"
            style={{ width: "100%", padding: "9px 12px 9px 32px", background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: T.textPrimary, fontSize: "14px", fontFamily: T.fontUI, outline: "none", boxSizing: "border-box" }}
          />
        </div>
        <select value={source} onChange={e => { setSource(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Sources</option>
          <option value="Grants.gov">Grants.gov</option>
          <option value="EU Funding Portal">EU Funding Portal</option>
          <option value="World Bank">World Bank</option>
          {status?.braveEnabled && <option value="Brave Search">Web Search</option>}
        </select>
        <select value={sector} onChange={e => { setSector(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Sectors</option>
          {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={grantType} onChange={e => { setGrantType(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Types</option>
          {GRANT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {(search || source || sector || grantType || matchMode) && (
          <button
            onClick={() => { setSearch(""); setSource(""); setSector(""); setGrantType(""); setMatchMode(false); setPage(1); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "9px 12px", background: "transparent", border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: T.textMuted, fontSize: "13px", fontFamily: T.fontUI, cursor: "pointer" }}
          >
            <Filter size={13} /> Clear
          </button>
        )}
      </div>

      {/* ── Grant grid ── */}
      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(440px, 100%), 1fr))", gap: "14px" }}>
          {Array.from({ length: 6 }).map((_, i) => {
            return (
              <div key={i} aria-hidden="true" style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusLg, padding: "20px", display: "flex", flexDirection: "column", gap: "12px", boxSizing: "border-box" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ ...shimmer, height: 16, flex: 1 }} />
                  <div style={{ ...shimmer, height: 16, width: 80 }} />
                </div>
                <div style={{ ...shimmer, height: 12, width: "55%" }} />
                <div style={{ display: "flex", gap: 6 }}>
                  <div style={{ ...shimmer, height: 18, width: 70, borderRadius: 9999 }} />
                  <div style={{ ...shimmer, height: 18, width: 90, borderRadius: 9999 }} />
                </div>
                <div style={{ ...shimmer, height: 12, width: "90%" }} />
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <div style={{ ...shimmer, height: 30, width: 72, borderRadius: 8 }} />
                  <div style={{ ...shimmer, height: 30, width: 110, borderRadius: 8 }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : grants.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: T.textMuted, fontFamily: T.fontUI }}>
          <Globe size={36} style={{ marginBottom: "12px", opacity: 0.25, display: "block", margin: "0 auto 12px" }} />
          <p style={{ fontSize: "15px", margin: "0 0 6px" }}>No grants found{search ? ` for "${search}"` : ""}.</p>
          <p style={{ fontSize: "13px", color: T.textMuted, margin: "0 0 18px" }}>
            {(search || source || sector || grantType || matchMode)
              ? "Try broadening your filters, or fetch the latest opportunities."
              : "Fetch the latest opportunities from Grants.gov, the EU Funding Portal and the web."}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            {(search || source || sector || grantType || matchMode) && (
              <button
                onClick={() => { setSearch(""); setSource(""); setSector(""); setGrantType(""); setMatchMode(false); setPage(1); }}
                style={{ padding: "9px 18px", background: "transparent", border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: T.textSecondary, fontSize: "13px", fontFamily: T.fontUI, cursor: "pointer" }}>
                Clear filters
              </button>
            )}
            <button
              onClick={handleTrigger}
              disabled={triggering || scanning}
              style={{ padding: "9px 18px", background: TOKENS.gold, border: "none", borderRadius: T.radiusMd, color: "#fff", fontSize: "13px", fontWeight: 600, fontFamily: T.fontUI, cursor: (triggering || scanning) ? "not-allowed" : "pointer", opacity: (triggering || scanning) ? 0.6 : 1 }}>
              {scanning ? "Scanning…" : "Run discovery now"}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(440px, 100%), 1fr))", gap: "14px" }}>
          {grants.map(g => (
            <GrantCard
              key={g.id || g.url}
              grant={g}
              onImport={handleImport}
              onExtract={handleExtract}
              matchMode={matchMode}
            />
          ))}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", marginTop: "32px" }}>
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            style={{ display: "flex", alignItems: "center", gap: "4px", padding: "8px 14px", background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: page <= 1 ? T.textDisabled : T.textSecondary, fontSize: "13px", fontFamily: T.fontUI, cursor: page <= 1 ? "not-allowed" : "pointer" }}
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <span style={{ fontSize: "13px", color: T.textMuted, fontFamily: T.fontUI }}>
            Page {page} of {totalPages} · {total.toLocaleString()} grants
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            style={{ display: "flex", alignItems: "center", gap: "4px", padding: "8px 14px", background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusMd, color: page >= totalPages ? T.textDisabled : T.textSecondary, fontSize: "13px", fontFamily: T.fontUI, cursor: page >= totalPages ? "not-allowed" : "pointer" }}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}

      </>)}

      {/* shimmer + the reduced-motion reset live in App.css (global) */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
      `}</style>
    </div>
  );
}
